<template>
  <div class="mini-tab-content">
    <div
      v-if="loading"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="secondary"
      />
      <span>{{ $t('involvedPanel.classification.loading') }}</span>
    </div>

    <template v-else>
      <div
        v-if="classifications.length"
        class="cls-chips"
      >
        <button
          v-for="c in classifications"
          :key="c.id"
          type="button"
          class="cls-chip"
          :class="{ 'cls-chip--on': selected === c.id }"
          :style="{ borderColor: c.hex_color, color: selected === c.id ? chipTextColor(c.hex_color) : '#000', backgroundColor: selected === c.id ? c.hex_color : '#fff' }"
          :disabled="saving || readonly"
          @click="choose(c.id)"
        >
          {{ c.color_label }}
        </button>

        <button
          v-if="selected && !readonly"
          type="button"
          class="interc-item__del"
          :disabled="saving || readonly"
          :title="$t('involvedPanel.classification.clear')"
          @click="confirmClear"
        >
          <b-spinner
            v-if="saving"
            small
          />
          <feather-icon
            v-else
            icon="Trash2Icon"
            size="14"
          />
        </button>
      </div>

      <small
        v-if="error"
        class="text-danger d-block mt-1"
      >
        {{ error }}
        <a
          href="#"
          class="ml-50"
          @click.prevent="load"
        >{{ $t('involvedPanel.classification.retryLink') }}</a>
      </small>
      <small
        v-else-if="!classifications.length"
        class="text-muted"
      >
        {{ $t('involvedPanel.classification.noneAvailable') }}
      </small>

      <hr class="my-2">

      <div
        v-if="loadingHistory"
        class="interc-loading"
      >
        <b-spinner
          small
          variant="secondary"
        />
        <span>{{ $t('involvedPanel.classification.history.loading') }}</span>
      </div>

      <div
        v-else-if="!history.length"
        class="text-muted small"
      >
        {{ $t('involvedPanel.classification.history.empty') }}
      </div>

      <div
        v-else
        class="interc-list"
      >
        <div
          v-for="h in history"
          :key="h.id"
          :class="['interc-item', { 'interc-item--cancelled': h.cancelled_at }]"
        >
          <div class="interc-item__top">
            <div class="interc-item__badge">
              <i
                v-if="!h.cancelled_at"
                class="cls-history-dot mr-25"
                :style="{ backgroundColor: h.classification.hex_color }"
              />
              <feather-icon
                v-else
                icon="XCircleIcon"
                size="11"
                class="mr-25"
              />
              {{ h.classification.color_label }}
            </div>
          </div>
          <div class="interc-item__meta">
            <feather-icon
              icon="UserIcon"
              size="11"
            />
            <span>{{ h.user ? h.user.name : '—' }}</span>
            <span class="interc-item__dot" />
            <feather-icon
              icon="ClockIcon"
              size="11"
            />
            <span>{{ h.created_at | formatDate }}</span>
          </div>
          <div
            v-if="h.cancelled_at"
            class="interc-item__cancelled-row"
          >
            <feather-icon
              icon="XCircleIcon"
              size="11"
              class="mr-25"
            />
            {{ $t('involvedPanel.classification.history.cancelledBy') }}&nbsp;<strong>{{ h.cancelled_by ? h.cancelled_by.name : '—' }}</strong>
            · {{ h.cancelled_at | formatDate }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import axios from 'axios'
import involvedPanelMixin from './involvedPanelMixin'
import { getContrastTextColor } from '@/utils/color'

export default {
  name: 'ClassificationPanel',

  components: { BSpinner },

  mixins: [involvedPanelMixin],

  props: {
    involvedClassificationId: { type: [Number, String], default: null },
  },

  data() {
    return {
      classifications: [],
      selected: this.involvedClassificationId ? Number(this.involvedClassificationId) : null,
      loading: false,
      saving: false,
      error: null,
      history: [],
      loadingHistory: false,
    }
  },

  watch: {
    occurrenceId() {
      this.load()
    },
    involvedClassificationId(value) {
      this.selected = value ? Number(value) : null
    },
  },

  mounted() {
    this.load()
    this.loadHistory()
  },

  methods: {
    // Chamado pelo mixin ao reativar a aba (keep-alive): evita dados velhos sem F5.
    refresh() {
      return Promise.all([this.load(), this.loadHistory()])
    },

    async load() {
      if (!this.occurrenceId) return
      this.loading = true
      this.error = null
      try {
        const res = await axios.get(`${this.$store.state.api}classifications/for-occurrence/${this.occurrenceId}`)
        this.classifications = res.data.data || []
      } catch (e) {
        this.classifications = []
        this.error = this.$t('involvedPanel.classification.loadError')
      } finally {
        this.loading = false
      }
    },

    async loadHistory() {
      if (!this.occurrenceInvolvedId) return
      this.loadingHistory = true
      try {
        const res = await axios.get(
          `${this.$store.state.api}occurrence_involveds/${this.occurrenceInvolvedId}/classification-history`,
        )
        this.history = res.data.data || []
      } catch (e) {
        this.history = []
      } finally {
        this.loadingHistory = false
      }
    },

    choose(id) {
      if (this.saving || this.selected === id) return
      this.change(id)
    },

    chipTextColor(hex) {
      return getContrastTextColor(hex)
    },

    async confirmClear() {
      if (this.saving) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('involvedPanel.classification.messages.deleteConfirm.body'),
        {
          title: this.$t('involvedPanel.classification.messages.deleteConfirm.title'),
          okTitle: this.$t('involvedPanel.classification.buttons.deleteConfirm'),
          cancelTitle: this.$t('involvedPanel.classification.buttons.cancel'),
          okVariant: 'danger',
          centered: true,
          size: 'sm',
        },
      )
      if (!confirmed) return
      this.change(null)
    },

    async change(id) {
      const previous = this.selected
      this.selected = id
      this.saving = true
      this.error = null
      try {
        const res = await axios.post(
          `${this.$store.state.api}occurrence_involveds/${this.occurrenceInvolvedId}/classification`,
          { classification_id: id },
        )
        this.$emit('changed', res.data.data.classification)
        this.loadHistory()
      } catch (e) {
        this.selected = previous
        this.error = (e.response && typeof e.response.data === 'string')
          ? e.response.data
          : this.$t('involvedPanel.classification.saveError')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.cls-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.cls-chip {
  padding: 6px 14px;
  border: 1.5px solid;
  border-radius: 14px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s, color .15s;
}

.cls-chip:disabled { cursor: default; opacity: .65; }

.cls-history-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
